import { useEffect, useState, useRef, useMemo } from "react"
import { get } from "../store/data/actions"
import { unstable_batchedUpdates } from "react-dom"
import { useHistory } from "react-router"
import { parseQuery, stringifyQuery } from "./useQuery"
import { iBasicQuery, iResponse } from "../definitions/interfaces"
import { AxiosError, AxiosResponse } from "axios"

export interface iUseData<T> {
  link: string
  defaultQuery?: T
  shouldPushQuery?: boolean
  shouldPingForData?: boolean
  shouldListenForHistory?: boolean
  shouldQueryOnStart?: boolean
}

export interface iUseDataResult<T, K> {
  total: number
  data: Array<K>
  error?: AxiosResponse
  setQuery(query: T, resetQuery?: boolean): void
  loading: boolean
  query: T
}

export function useData<T extends iBasicQuery, K>({
  link,
  defaultQuery,
  shouldPushQuery,
  shouldPingForData = true,
  shouldListenForHistory = true,
  shouldQueryOnStart = true,
}: iUseData<T>): iUseDataResult<T, K> {
  const [total, setTotal] = useState(0)
  const [data, setData] = useState<Array<K>>([])
  const [error, setError] = useState<AxiosResponse>()
  const [loading, setLoading] = useState(true)
  const [query, setPublicQuery] = useState(defaultQuery as T)
  const history = useHistory()
  const _routerListener = useRef<() => void>()
  const _destroyed = useRef(false)
  const _query = useRef<T>(defaultQuery as T)
  const _timeout = useRef<NodeJS.Timeout>()
  const _interval = useRef<NodeJS.Timeout>()
  const _loading = useRef<boolean>(loading)

  const stopInterval = () => {
    _interval.current && clearInterval(_interval.current)
  }

  const getData = (query: T, resetQuery = false) => {
    if (_destroyed.current) return

    get<iResponse<Array<K>>>({
      link,
      query,
    })
      .then(({ data = [], total }) => {
        if (_destroyed.current) return
        unstable_batchedUpdates(() => {
          setLoading(false)
          setData(data)
          setTotal(total || 0)
          if (resetQuery) {
            setPublicQuery(query as T)
          }
        })
        createInterval()
      })
      .catch((err) => {
        setError(err)
      })
  }
  const createInterval = () => {
    if (!shouldPingForData) return
    _interval.current && clearInterval(_interval.current)
    _interval.current = setInterval(() => {
      if (_loading.current) return

      get<iResponse<Array<K>>>({ link, query: _query.current })
        .then(({ data = [], total }) => {
          unstable_batchedUpdates(() => {
            setLoading(false)
            setData(data)
            setTotal(total || 0)
          })
        })
        .catch((error: AxiosError) => {
          setError(error.response)
        })
    }, 5000)
  }

  const batchUpdate = (query: T, resetQuery = false) => {
    unstable_batchedUpdates(() => {
      getData(query, resetQuery)
      _query.current = query
    })
  }

  const setQuery = (query: T, resetQuery = false) => {
    if (_destroyed.current) return
    clearTimeout(_timeout.current as NodeJS.Timeout)

    !_loading.current && setLoading(true)

    if (_query.current && _query.current.page && query && !query.page)
      query.page = 1

    const tmp = resetQuery ? query : { ..._query.current, ...query }

    if (resetQuery) {
      batchUpdate(tmp, resetQuery)
      history.replace({
        pathname: history.location.pathname,
        search: stringifyQuery(tmp),
      })
    } else {
      _timeout.current = setTimeout(() => {
        batchUpdate(tmp)
        if (shouldPushQuery) {
          console.log(tmp)

          history.push({
            pathname: history.location.pathname,
            search: stringifyQuery(tmp),
          })
        }
      }, 500)
    }
  }

  useMemo(() => {
    if (shouldQueryOnStart) {
      if (history.location.search) {
        setQuery(parseQuery(location.search), true)
      } else if (defaultQuery) {
        setQuery(defaultQuery, true)
      } else {
        setQuery({} as T, true)
      }
    }
  }, [])

  useEffect(() => {
    if (shouldListenForHistory) {
      _routerListener.current = history.listen((location) => {
        if (history.action === "POP") {
          const tmp = parseQuery(location.search)
          setQuery(tmp, true)
        }
      })
    }
    return () => {
      _destroyed.current = true
      _routerListener.current && _routerListener.current()
      stopInterval()
    }
  }, [])

  useEffect(() => {
    _loading.current = loading
  }, [loading])

  return { total, data, setQuery, loading, query, error }
}
